import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import type { AdminUserAuditEntry } from '../../generated/api/types.gen.js';
import { getUserAuditLogOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, Callout, DataTable, EmptyState } from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';

interface UserActivityTabProps {
  userId: number;
}

const ACTION_LABELS: Record<string, string> = {
  status_changed: 'Status changed',
  role_assigned: 'Role assigned',
  role_removed: 'Role removed',
  password_reset: 'Password reset initiated',
  profile_updated: 'Profile updated',
  email_verified: 'Email verified',
  account_activated: 'Account activated',
  account_deactivated: 'Account deactivated',
  login: 'Logged in',
  login_failed: 'Login failed',
  logout: 'Logged out',
  created: 'Account created',
  invitation_sent: 'Invitation sent',
  invitation_accepted: 'Invitation accepted',
  org_joined: 'Joined organization',
  org_left: 'Left organization',
  settings_updated: 'Settings updated',
  api_key_created: 'API key created',
  api_key_revoked: 'API key revoked',
};

function formatActionLabel(action: string): string {
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function formatTimestamp(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDetails(details: Record<string, unknown>): string {
  if (!details || Object.keys(details).length === 0) return '-';

  // Common patterns
  if (details.from && details.to) {
    return `${String(details.from)} \u2192 ${String(details.to)}`;
  }
  if (details.role && details.orgName) {
    return `${String(details.role)} in ${String(details.orgName)}`;
  }
  if (details.role) {
    return String(details.role);
  }
  if (details.status) {
    return `Status: ${String(details.status)}`;
  }
  if (details.message) {
    return String(details.message);
  }
  if (details.reason) {
    return String(details.reason);
  }
  if (details.field) {
    return `Field: ${String(details.field)}`;
  }
  if (details.ip) {
    return `IP: ${String(details.ip)}`;
  }

  // Fallback: render key=value pairs
  const entries = Object.entries(details)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .slice(0, 3);
  if (entries.length === 0) return '-';
  return entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ');
}

export const UserActivityTab: React.FC<UserActivityTabProps> = ({ userId }) => {
  const { data, isPending, isError, refetch } = useQuery(getUserAuditLogOptions({ path: { id: userId } }));

  const columns: DataTableColumn<AdminUserAuditEntry>[] = [
    {
      key: 'createdAt',
      header: 'When',
      sortable: true,
      sortValue: (entry) => entry.createdAt,
      render: (entry) => (
        <span className="whitespace-nowrap text-sm text-muted">{formatTimestamp(entry.createdAt)}</span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (entry) => <span className="text-sm font-medium text-heading">{formatActionLabel(entry.action)}</span>,
    },
    {
      key: 'actor',
      header: 'Done by',
      render: (entry) => <span className="text-sm text-body">{entry.actorName || `User #${entry.actorId}`}</span>,
    },
    {
      key: 'details',
      header: 'Details',
      render: (entry) => <span className="line-clamp-2 text-sm text-muted">{formatDetails(entry.details)}</span>,
    },
  ];

  if (isError) {
    return (
      <Callout
        tone="danger"
        title="Could not load activity"
        actions={
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Callout>
    );
  }

  return (
    <DataTable
      framed
      columns={columns}
      rows={data?.data ?? []}
      rowKey={(entry) => entry.id}
      loading={isPending}
      pageSize={20}
      aria-label="Account activity"
      empty={
        <EmptyState
          icon={History}
          title="No activity yet"
          description="Sign-ins, role changes and profile edits for this account are recorded here."
        />
      }
    />
  );
};
